// Pure scroll-restoration decision logic, kept DOM-free so the
// navtype → action rule is unit-tested without a browser (mirrors the
// pure-helper pattern in board/boardScrollCollapse.ts).
//
// The rule is the SPA-standard one:
//   - Back / forward (POP) with a remembered offset → restore that offset.
//   - A fresh navigation (PUSH) or a redirect (REPLACE) → reset to the top.
//
// This is what fixes the "enter a board fresh from My People and land
// half-scrolled" bug (PUSH → top) while still returning the user to where
// they were when they hit back from the Saved board (POP → restore).

export type NavType = 'PUSH' | 'POP' | 'REPLACE';

export type ScrollAction =
  | { kind: 'restore'; top: number }
  | { kind: 'reset' };

/**
 * Decide what to do with scroll on a navigation, given the navigation type
 * and any offset previously remembered for the destination history entry.
 *
 * A remembered offset is only honored on POP (back/forward) — the one case
 * where "return me to where I was" is the expected behavior. PUSH/REPLACE
 * always reset to the top, even if an offset happens to be on record, because
 * arriving somewhere new should start at the top. An offset of 0 is treated
 * as a reset since the two are visually identical.
 */
export function scrollActionFor(
  navType: NavType,
  savedTop: number | undefined,
): ScrollAction {
  if (navType === 'POP' && typeof savedTop === 'number' && savedTop > 0) {
    return { kind: 'restore', top: savedTop };
  }
  return { kind: 'reset' };
}

/**
 * Decide what to do with scroll for a full history transition — i.e. taking
 * into account WHETHER this is even a page navigation, not just the navtype.
 * Returns `null` for "leave scroll exactly as-is, touch nothing".
 *
 * The scroll-restoration boundary is a PATHNAME change, NOT a history-entry
 * change. Two same-page transitions must be transparent to restoration:
 *
 *   - An in-page anchor (`#hash`) — the page's own anchor scroll should win.
 *   - A same-pathname transition — an overlay or param toggle layered on the
 *     page the user is already on (the product-detail sheet's `?pdp=N` depth,
 *     a filter param, a share/about sheet wired into history). The underlying
 *     page never unmounts, so its scroll must be left untouched: NOT reset when
 *     the overlay opens (PUSH), NOT re-animated when it closes (POP). Treating
 *     these as navigations is what made closing a PDP visibly fling the feed
 *     from the top back down to the item.
 *
 * Only a genuine route change (different pathname) falls through to the
 * standard PUSH-resets / POP-restores rule in `scrollActionFor`.
 */
export function scrollActionForTransition(input: {
  navType: NavType;
  /** Destination pathname equals the pathname we navigated from. */
  samePathname: boolean;
  /** Destination carries a `#fragment`. */
  hasHash: boolean;
  savedTop: number | undefined;
}): ScrollAction | null {
  if (input.hasHash) return null;
  if (input.samePathname) return null;
  return scrollActionFor(input.navType, input.savedTop);
}

/**
 * Whether the DESTINATION entry of a transition should be seeded with the
 * current scroll offset (filed under the new entry's key).
 *
 * Same-pathname PUSH/REPLACE entries are overlays layered onto the page the
 * user is already on — the PDP's `?pdp=N` depth, a filter param. Scroll-wise
 * they're transparent (see scrollActionForTransition), so no offset would ever
 * be recorded under them: the user's scrolling happened on the entry BENEATH.
 * That's fine while the SPA stays alive — but the mobile click-out navigates
 * the SAME TAB to the merchant, and Back (without bfcache) cold-reloads the app
 * ON the overlay entry. The PDP rehydrates from its sessionStorage stack, but
 * the feed beneath boots at the top with nothing on record to restore, so
 * closing the sheet stranded the user at the top of the board.
 *
 * Seeding the overlay entry with the offset it was opened over closes that
 * hole: the cold reload's POP-restore finds the feed offset under the overlay
 * entry's own key and puts the board back where it was, behind the sheet.
 *
 * POP is deliberately excluded — a transparent close must not overwrite the
 * origin entry's remembered offset (e.g. with a short post-reload position).
 */
export function seedsScrollForTransition(input: {
  navType: NavType;
  samePathname: boolean;
  hasHash: boolean;
}): boolean {
  if (input.hasHash) return false;
  if (!input.samePathname) return false;
  return input.navType === 'PUSH' || input.navType === 'REPLACE';
}
